import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orders as ordersApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Package, CheckCircle2, PackageCheck, Truck, MapPin, Phone, Share2,
  Store as StoreIcon, Clock, XCircle, Loader2, ShieldCheck, RefreshCw,
} from 'lucide-react';

const STEP_META = [
  { key: 'placed',           label: 'Order placed',       Icon: Package,      color: '#0D47A1' },
  { key: 'accepted',         label: 'Confirmed',          Icon: CheckCircle2, color: '#1B5E20' },
  { key: 'packed',           label: 'Packed & ready',     Icon: PackageCheck, color: '#7A4A00' },
  { key: 'out_for_delivery', label: 'Out for delivery',   Icon: Truck,        color: '#D35400' },
  { key: 'delivered',        label: 'Delivered',          Icon: MapPin,       color: '#1B5E20' },
];

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function Track() {
  const { id } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState(null);

  const load = async () => {
    try {
      const t = await ordersApi.track(id);
      setData(t);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load tracking');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);   // live polling every 15s
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: 'Track my BharatCart order', url }); return; } catch { /* dismissed */ }
    }
    await navigator.clipboard.writeText(url);
    toast.success('Tracking link copied — paste in WhatsApp!');
  };

  if (loading && !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Loader2 className="w-10 h-10 mx-auto text-[#1B5E20] animate-spin"/>
        <div className="mt-4 text-ink/60">Loading your order tracking…</div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <XCircle className="w-14 h-14 mx-auto text-[#BF360C]"/>
        <h1 className="font-heading font-black text-2xl mt-4">Order not found</h1>
        <p className="text-ink/60 mt-2">{err || 'Please double-check your tracking link.'}</p>
        <Link to="/" className="btn-primary mt-6 inline-flex">Back to home</Link>
      </div>
    );
  }

  const isCancelled = data.status === 'cancelled';
  const isDelivered = data.status === 'delivered';
  const currentIdx  = data.current_step;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10" data-testid="track-page">
      {/* Hero */}
      <div className={`rounded-3xl overflow-hidden p-6 md:p-8 text-white ${isCancelled ? 'bg-gradient-to-br from-[#BF360C] to-[#E64A19]' : isDelivered ? 'bg-gradient-to-br from-[#1B5E20] to-[#66BB6A]' : 'bg-gradient-to-br from-[#0D3B15] to-[#1B5E20]'}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold opacity-70">Order tracking</div>
            <div className="mt-1 font-mono text-xs opacity-70">#{data.short_id}</div>
            <h1 data-testid="track-status-label" className="mt-2 font-heading font-black text-3xl md:text-4xl leading-tight">
              {data.status_label}
            </h1>
            {!isCancelled && !isDelivered && data.eta_to && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-sm">
                <Clock className="w-4 h-4"/>
                Expected between <b>{fmtDateTime(data.eta_from)}</b> &nbsp;–&nbsp; <b>{fmtDateTime(data.eta_to)}</b>
              </div>
            )}
            {isDelivered && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-sm">
                <CheckCircle2 className="w-4 h-4"/>Successfully delivered — thank you!
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button data-testid="track-refresh" onClick={load} className="rounded-full bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5"/>Refresh</button>
            <button data-testid="track-share" onClick={share} className="rounded-full bg-white text-[#1B5E20] hover:bg-white/90 px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5"/>Share</button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-6 card-soft p-5 md:p-7">
        <div className="font-heading font-bold text-lg mb-5">Shipment timeline</div>

        {isCancelled ? (
          <div data-testid="track-cancelled" className="rounded-2xl border-2 border-[#FFAB91] bg-[#FBE9E7] p-5 flex items-start gap-3">
            <XCircle className="w-6 h-6 text-[#BF360C] flex-shrink-0 mt-0.5"/>
            <div>
              <div className="font-heading font-bold text-[#BF360C]">Order cancelled</div>
              <div className="text-sm text-ink/70 mt-1">This order was cancelled{data.cancelled_at ? ` on ${fmtDateTime(data.cancelled_at)}` : ''}. Any payment made will be refunded within 5-7 business days.</div>
            </div>
          </div>
        ) : (
          <ol className="relative">
            {STEP_META.map((step, i) => {
              const done   = i < currentIdx || (i === currentIdx && isDelivered);
              const active = i === currentIdx && !isDelivered;
              const upcoming = i > currentIdx;
              const Icon = step.Icon;
              return (
                <li key={step.key} data-testid={`track-step-${step.key}`} className="relative pl-14 pb-7 last:pb-0">
                  {/* Connector line */}
                  {i < STEP_META.length - 1 && (
                    <span className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${done ? 'bg-[#1B5E20]' : 'bg-border'}`}/>
                  )}
                  {/* Dot / Icon */}
                  <div className={`absolute left-0 top-0 w-10 h-10 rounded-full grid place-items-center border-2 transition ${
                    done   ? 'bg-[#1B5E20] border-[#1B5E20] text-white' :
                    active ? 'bg-white border-[#D35400] text-[#D35400] shadow-[0_0_0_6px_rgba(230,126,34,0.15)] animate-pulse' :
                             'bg-white border-border text-ink/30'
                  }`}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  {/* Text */}
                  <div className="pt-1">
                    <div className={`font-heading font-bold text-base ${
                      done ? 'text-[#1B5E20]' : active ? 'text-[#D35400]' : 'text-ink/40'
                    }`}>
                      {step.label}
                      {active && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FFF3E0] text-[#D35400] px-2 py-0.5 rounded-full">Current</span>}
                    </div>
                    {done && i === 0 && data.created_at && (
                      <div className="text-xs text-ink/50 mt-0.5">{fmtDateTime(data.created_at)}</div>
                    )}
                    {active && data.eta_to && (
                      <div className="text-xs text-ink/60 mt-0.5">Expected next by <b>{fmtDateTime(data.eta_to)}</b></div>
                    )}
                    {upcoming && (
                      <div className="text-xs text-ink/40 mt-0.5">Pending…</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Info cards */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {/* Store */}
        <div className="card-soft p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Shipping from</div>
          <div className="mt-2 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E3F2FD] text-[#0D47A1] grid place-items-center flex-shrink-0"><StoreIcon className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-base">{data.store.name || '—'}</div>
              <div className="text-xs text-ink/60">{data.store.village}{data.store.pincode ? ` · PIN ${data.store.pincode}` : ''}</div>
              {data.store.phone && (
                <a href={`tel:${data.store.phone}`} data-testid="track-store-call" className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#1B5E20] font-semibold hover:underline">
                  <Phone className="w-3.5 h-3.5"/>Call store
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Delivery agent OR recipient */}
        <div className="card-soft p-5">
          {data.agent ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D35400]">Delivery partner</div>
              <div className="mt-2 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFF3E0] text-[#D35400] grid place-items-center flex-shrink-0"><Truck className="w-5 h-5"/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base">{data.agent.name}</div>
                  <div className="text-xs text-ink/60">Phone ends •••• {data.agent.phone_last4}</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B5E20]">Recipient</div>
              <div className="mt-2 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] text-[#1B5E20] grid place-items-center flex-shrink-0"><MapPin className="w-5 h-5"/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base">{data.recipient_first_name}</div>
                  <div className="text-xs text-ink/60">{data.recipient_village || '—'}</div>
                  <div className="text-[10px] text-ink/40 mt-1">Personal details are hidden for privacy.</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* OTP notice */}
        {data.otp_required && !data.otp_verified && !isDelivered && (
          <div className="md:col-span-2 rounded-2xl border-2 border-[#FFCC80] bg-[#FFF3E0] p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#9A4500] flex-shrink-0 mt-0.5"/>
            <div className="text-sm text-[#9A4500]">
              <div className="font-semibold">Delivery OTP required</div>
              <div className="text-xs mt-1">This order contains important goods. Please share the 6-digit OTP with the delivery agent only when they hand over your package.</div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="md:col-span-2 card-soft p-5">
          <div className="flex items-center justify-between text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/50">Order summary</div>
              <div className="mt-1"><b>{data.items_count}</b> item{data.items_count === 1 ? '' : 's'} · Placed <b>{fmtDateTime(data.created_at)}</b></div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/50">Total</div>
              <div className="font-heading font-black text-2xl text-[#1B5E20] mt-0.5">{inr(data.total)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-ink/40">
        Sold via <b className="text-[#1B5E20]">BharatCart</b> — your village shop, online.
      </div>
    </div>
  );
}
